import { derived } from 'svelte/store';
import { createQuery, type QueryClient } from '$lib/api/tanstack-query';

import type { AuthContext } from '$lib/auth';

import type { AppServiceClient } from '../client';
import { prefetchQuery } from '../prefetch';
import type { BaseQueryOptions } from '../query-options';
import {
  createQueryOptions,
  getAuthedQueryOptions,
  useAuthedQueryOptions,
} from '../query-options';
import { orgKeys } from './query-key';

const LIST_ORGANIZATIONS_STALE_TIME_MS = 10_000;

/** Create a query for `listOrganizations`. */
export const createListOrganizationsQuery = () => {
  const baseQueryOptions = useAuthedQueryOptions('app');
  const options = derived(baseQueryOptions, ($baseQueryOptions) =>
    listOrganizationsQueryOptions($baseQueryOptions)
  );

  return createQuery(options);
};

/** Call `listOrganizations` and prime the query cache. */
export const prefetchListOrganizations = async (
  queryClient: QueryClient,
  client: AppServiceClient,
  authContext: AuthContext
) => {
  const baseOptions = getAuthedQueryOptions(client, authContext);
  const options = listOrganizationsQueryOptions(baseOptions);

  return prefetchQuery(queryClient, options);
};

const listOrganizationsQueryOptions = ({
  client,
  getRPCOptions,
  enabled,
}: BaseQueryOptions<AppServiceClient>) => {
  return createQueryOptions({
    enabled,
    queryKey: orgKeys.list(),
    staleTime: LIST_ORGANIZATIONS_STALE_TIME_MS,
    queryFn: async () => {
      const rpcOptions = await getRPCOptions();
      const { response } = await client.listOrganizations({}, rpcOptions);

      return response.organizations;
    },
  });
};
